import React, { useEffect, useMemo, useRef } from 'react';
import { Form, Input, Select, Checkbox, Alert, Typography } from 'antd';
import type { FormInstance } from 'antd';
import LookupSelect from './LookupSelect';
import { Organization, User } from '../services/adminService';
import { useAuth } from '../auth/AuthContext';

const { Text } = Typography;

// KAN-38: per-role defaults for the two SIM-visibility flags. Mirrors the
// backend ROLE_DEFAULTS_VISIBILITY in lambda/admin/routes/users.js so a freshly
// picked role pre-populates the same checkboxes the backend would apply on
// submit. Defined here so the create form can suggest sensible defaults
// without round-tripping to the server.
const ROLE_VISIBILITY_DEFAULTS: Record<
  string,
  { canViewSubordinateSims: boolean; canViewYachtSims: boolean }
> = {
  super_admin: { canViewSubordinateSims: true, canViewYachtSims: true },
  reseller_admin: { canViewSubordinateSims: true, canViewYachtSims: true },
  reseller_support: { canViewSubordinateSims: true, canViewYachtSims: true },
  customer_admin: { canViewSubordinateSims: false, canViewYachtSims: true },
  asset_manager: { canViewSubordinateSims: true, canViewYachtSims: true },
  department_head: { canViewSubordinateSims: true, canViewYachtSims: false },
  crew: { canViewSubordinateSims: false, canViewYachtSims: false },
};

// Mirrors backend ALLOWED_CHILD_ROLES in lambda/admin/lib/auth.js. The backend
// is the source of truth and will 403 a bad request; this table just stops the
// UI from showing options the caller can't actually pick.
export const ALLOWED_CHILD_ROLES: Record<string, string[]> = {
  super_admin: [
    'super_admin',
    'reseller_admin',
    'reseller_support',
    'customer_admin',
    'asset_manager',
    'department_head',
    'crew',
  ],
  reseller_admin: [
    'reseller_support',
    'customer_admin',
    'asset_manager',
    'department_head',
    'crew',
  ],
  customer_admin: ['asset_manager', 'department_head', 'crew'],
  asset_manager: ['department_head', 'crew'],
  department_head: [],
  reseller_support: [],
  crew: [],
};

export type UserFormMode = 'create' | 'edit';

export interface UserFormValues {
  email?: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: number;
  department?: string;
  canViewSubordinateSims?: boolean;
  canViewYachtSims?: boolean;
}

interface UserFormProps {
  form: FormInstance<UserFormValues>;
  mode: UserFormMode;
  organizations: Organization[];
  initialValues?: Partial<UserFormValues> | User;
  // Force a specific organization (used when adding a user from inside an org
  // detail page). When set, the org select is hidden and the value pinned.
  fixedOrganizationId?: number;
  // Roles the caller is allowed to assign. If undefined, LookupSelect uses
  // its default list and the backend will enforce.
  assignableRoles?: string[];
  onFinish?: (values: UserFormValues) => void;
  onValuesChange?: (changed: any, all: UserFormValues) => void;
}

// MySQL tinyint(1) values arrive from the API as numeric 0/1. AntD Checkbox
// renders them correctly but feeds them back into the form unchanged, so the
// PUT body ends up with `canView*: 0` and the backend's bool-only validator
// 400s. Coerce here so the form always holds true/false/undefined.
function coerceBool(v: unknown): boolean | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'boolean') return v;
  return Boolean(v);
}

function normaliseInitial(initial: any): Partial<UserFormValues> {
  if (!initial) return {};
  // Accept either snake_case (User from API) or camelCase (form values).
  return {
    email: initial.email,
    firstName: initial.firstName ?? initial.first_name,
    lastName: initial.lastName ?? initial.last_name,
    role: initial.role,
    organizationId: initial.organizationId ?? initial.organization_id,
    department: initial.department ?? '',
    canViewSubordinateSims: coerceBool(
      initial.canViewSubordinateSims ?? initial.can_view_subordinate_sims
    ),
    canViewYachtSims: coerceBool(initial.canViewYachtSims ?? initial.can_view_yacht_sims),
  };
}

const UserForm: React.FC<UserFormProps> = ({
  form,
  mode,
  organizations,
  initialValues,
  fixedOrganizationId,
  onFinish,
  onValuesChange,
}) => {
  const auth = useAuth();
  const callerRole = auth.role || '';
  // Allowed roles for the role picker. In edit mode we additionally include the
  // target user's current role even if the caller could not have created it
  // (so the form does not blank out the value); the backend still rejects
  // changes outside ALLOWED_CHILD_ROLES.
  const allowedRoles = useMemo(() => {
    const base = ALLOWED_CHILD_ROLES[callerRole] || [];
    const current = (initialValues as any)?.role;
    if (mode === 'edit' && current && !base.includes(current)) {
      return [...base, current];
    }
    return base;
  }, [callerRole, mode, initialValues]);

  // Track whether the user has manually touched a flag checkbox. If so, we
  // stop auto-applying role defaults when the role changes — so the explicit
  // choice is honoured.
  const flagsManuallyTouched = useRef({ sub: false, yacht: false });

  useEffect(() => {
    const initial = normaliseInitial(initialValues);
    if (fixedOrganizationId !== undefined) {
      initial.organizationId = fixedOrganizationId;
    }
    // In create mode, if the caller only has one org in their scope (typical
    // customer_admin case), pre-select it so they aren't blocked by an empty
    // "No data" dropdown.
    if (
      mode === 'create' &&
      initial.organizationId === undefined &&
      fixedOrganizationId === undefined &&
      organizations.length === 1
    ) {
      initial.organizationId = organizations[0].id;
    }
    // In create mode with no explicit flag values, pre-fill from the chosen
    // role's defaults so the form lands on a sensible starting state.
    if (mode === 'create' && initial.role) {
      const defaults = ROLE_VISIBILITY_DEFAULTS[initial.role];
      if (defaults) {
        if (initial.canViewSubordinateSims === undefined)
          initial.canViewSubordinateSims = defaults.canViewSubordinateSims;
        if (initial.canViewYachtSims === undefined)
          initial.canViewYachtSims = defaults.canViewYachtSims;
      }
    }
    form.setFieldsValue(initial);
    flagsManuallyTouched.current = { sub: false, yacht: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, fixedOrganizationId, mode, organizations]);

  const handleValuesChange = (changed: any, all: UserFormValues) => {
    // Mark flags as "touched" when the user changes them directly.
    if ('canViewSubordinateSims' in changed) flagsManuallyTouched.current.sub = true;
    if ('canViewYachtSims' in changed) flagsManuallyTouched.current.yacht = true;

    // If the role changes and the user has not overridden a flag, re-apply
    // the role default for that flag.
    if ('role' in changed && changed.role) {
      const defaults = ROLE_VISIBILITY_DEFAULTS[changed.role];
      if (defaults) {
        const patch: Partial<UserFormValues> = {};
        if (!flagsManuallyTouched.current.sub)
          patch.canViewSubordinateSims = defaults.canViewSubordinateSims;
        if (!flagsManuallyTouched.current.yacht) patch.canViewYachtSims = defaults.canViewYachtSims;
        if (Object.keys(patch).length > 0) form.setFieldsValue(patch);
      }
    }

    onValuesChange?.(changed, all);
  };

  return (
    <Form<UserFormValues>
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      onFinish={onFinish}
    >
      {mode === 'create' && (
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter an email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="email@example.com" autoComplete="off" />
        </Form.Item>
      )}
      <Form.Item
        name="firstName"
        label="First Name"
        rules={[{ required: true, message: 'Please enter first name' }]}
      >
        <Input placeholder="First name" autoComplete="given-name" />
      </Form.Item>
      <Form.Item
        name="lastName"
        label="Last Name"
        rules={[{ required: true, message: 'Please enter last name' }]}
      >
        <Input placeholder="Last name" autoComplete="family-name" />
      </Form.Item>
      <Form.Item
        name="role"
        label="Global Role"
        tooltip="System-wide permission level. Controls which API actions the user can take and the scope of data they see. Distinct from 'Role on Asset' shown in Asset Assignments, which is just a per-yacht job label (captain, engineer, crew, etc.) and grants no permissions."
        rules={[{ required: true, message: 'Please select a role' }]}
      >
        <LookupSelect
          category="role"
          mode="select"
          allowedValues={allowedRoles}
          placeholder={allowedRoles.length === 0 ? 'No roles you can assign' : 'Select role'}
          disabled={allowedRoles.length === 0}
        />
      </Form.Item>
      {fixedOrganizationId === undefined && (
        <Form.Item
          name="organizationId"
          label="Organization"
          rules={[{ required: true, message: 'Please select an organization' }]}
        >
          <Select
            placeholder={
              organizations.length === 0 ? 'No organizations in your scope' : 'Select organization'
            }
            showSearch
            optionFilterProp="children"
            disabled={organizations.length === 0}
          >
            {organizations.map(org => (
              <Select.Option key={org.id} value={org.id}>
                {org.name} ({org.type})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item name="department" label="Department">
        <LookupSelect category="department" placeholder="Select or enter department" />
      </Form.Item>

      <Form.Item
        label="SIM visibility"
        style={{ marginBottom: 8 }}
        tooltip="Controls which SIMs this user can see. Defaults come from the selected role; override here if needed."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Form.Item name="canViewSubordinateSims" valuePropName="checked" noStyle>
            <Checkbox>
              Can view subordinates' SIMs
              <Text type="secondary" style={{ marginLeft: 6 }}>
                (sees SIMs assigned to users below this one in the hierarchy)
              </Text>
            </Checkbox>
          </Form.Item>
          <Form.Item name="canViewYachtSims" valuePropName="checked" noStyle>
            <Checkbox>
              Can view yacht-wide SIMs
              <Text type="secondary" style={{ marginLeft: 6 }}>
                (sees modems and other yacht infrastructure SIMs)
              </Text>
            </Checkbox>
          </Form.Item>
        </div>
      </Form.Item>

      {mode === 'edit' && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 4 }}
          message="Visibility flags do not auto-update when the role changes for an existing user. Adjust them explicitly if needed."
        />
      )}
    </Form>
  );
};

export default UserForm;
